/**
 * My Clients Section Component (SP Dashboard)
 *
 * Displays:
 * - Referral link for client invitations
 * - List of connected clients
 * - List of pending invitations
 *
 * Mirrors the FA dashboard MyClientsSection layout
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { Card } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Users } from 'lucide-react';
import { useClients } from '@/features/client-relationships';
import { invitationsApi } from '@/features/sp-client-management/api/invitationsApi';
import { useProviderReferralLink } from '../hooks/useProviderReferralLink';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { SPConnectedClientsList } from './SPConnectedClientsList';
import { SPPendingInvitationsList } from './SPPendingInvitationsList';
import { ReferralLinkPanel } from '@/shared/components/referral/ReferralLinkPanel';

type MyClientsSectionProps = {
  providerId: string;
  onInviteClick?: () => void;
  onMessageClick?: (clientId: string) => void;
};

/**
 * Calculate time remaining until resend is available
 */
const getTimeRemaining = (resendAvailableAt: string | null): number => {
  if (!resendAvailableAt) return 0;
  const availableTime = new Date(resendAvailableAt).getTime();
  const now = Date.now();
  return Math.max(0, Math.ceil((availableTime - now) / 1000));
};

export function MyClientsSection({ providerId, onInviteClick, onMessageClick }: MyClientsSectionProps) {
  const queryClient = useQueryClient();
  const [copied, setCopied] = useState(false);
  const [cooldowns, setCooldowns] = useState<Record<string, number>>({});
  const [resendingId, setResendingId] = useState<string | null>(null);
  const cooldownsInitialized = useRef(false);

  // Fetch clients from unified endpoint
  const { data: clientsData, isLoading: isLoadingClients } = useClients({
    page: 1,
    limit: 50, // Max allowed by backend validation
  });

  // Fetch pending invitations
  const { data: invitationsData, isLoading: isLoadingInvitations } = useQuery({
    queryKey: ['sp-invitations', providerId, 'PENDING'],
    queryFn: () => invitationsApi.getInvitations(providerId, 'PENDING'),
  });

  // Fetch referral link
  const {
    data: referralData,
    isLoading: isLoadingReferralLink,
    isError: isReferralLinkError,
  } = useProviderReferralLink();

  const clients = clientsData?.data || [];
  const invitations = invitationsData?.data || [];
  const referralLink = referralData?.referralLink;

  const connectedCount = clients.length;
  const pendingCount = invitations.length;
  const hasClients = connectedCount > 0;
  const hasPending = pendingCount > 0;
  const hasAny = hasClients || hasPending;

  const isLoading = isLoadingClients || isLoadingInvitations;

  // Initialize cooldowns from API response (only once when data loads)
  useEffect(() => {
    if (!invitations.length || cooldownsInitialized.current) return;

    const newCooldowns: Record<string, number> = {};
    let hasAnyCooldown = false;

    invitations.forEach((inv) => {
      if (!inv.canResend && inv.resendAvailableAt) {
        const remaining = getTimeRemaining(inv.resendAvailableAt);
        if (remaining > 0) {
          newCooldowns[inv.id] = remaining;
          hasAnyCooldown = true;
        }
      }
    });

    if (hasAnyCooldown) {
      setCooldowns(newCooldowns);
    }
    cooldownsInitialized.current = true;
  }, [invitations]);

  // Reset initialization flag when data changes (e.g., after refetch)
  useEffect(() => {
    cooldownsInitialized.current = false;
  }, [invitationsData]);

  // Countdown timer for cooldowns - runs independently
  useEffect(() => {
    const interval = setInterval(() => {
      setCooldowns((prev) => {
        const hasActiveCooldowns = Object.values(prev).some((t) => t > 0);
        if (!hasActiveCooldowns) return prev;

        const updated: Record<string, number> = {};
        let hasChanges = false;

        Object.entries(prev).forEach(([id, time]) => {
          if (time > 0) {
            updated[id] = time - 1;
            hasChanges = true;
          }
        });

        return hasChanges ? updated : prev;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, []); // Empty dependency - timer runs independently

  // Resend mutation
  const resendMutation = useMutation({
    mutationFn: (invitationId: string) =>
      invitationsApi.resend(providerId, invitationId),
    onMutate: (invitationId) => {
      setResendingId(invitationId);
    },
    onSuccess: (_data, invitationId: string) => {
      // Set 30-minute cooldown
      setCooldowns((prev) => ({ ...prev, [invitationId]: 30 * 60 }));
      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['sp-invitations', providerId] });
    },
    onError: (error: any, invitationId: string) => {
      // If error response contains cooldown info, set it
      if (error.response?.data?.retryAfter) {
        setCooldowns((prev) => ({
          ...prev,
          [invitationId]: error.response.data.retryAfter,
        }));
      }
    },
    onSettled: () => {
      setResendingId(null);
    },
  });

  const handleResend = useCallback(
    (invitationId: string) => {
      resendMutation.mutate(invitationId);
    },
    [resendMutation],
  );

  const handleCopyReferralLink = async () => {
    if (!referralLink) return;

    try {
      await navigator.clipboard.writeText(referralLink);
      setCopied(true);

      // Reset copied state after 2 seconds
      setTimeout(() => {
        setCopied(false);
      }, 2000);
    } catch (error) {
      console.error('Failed to copy referral link:', error);
    }
  };

  if (isLoading) {
    return (
      <Card className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg text-text-primary">My Clients</h3>
        </div>
        <div className="text-center py-8 text-text-tertiary">
          Loading clients...
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      {/* Header with Title and Invite Button */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg text-text-primary">My Clients</h3>
          {hasAny && (
            <p className="text-sm text-text-secondary mt-1">
              {connectedCount > 0 && `${connectedCount} ${connectedCount === 1 ? 'client' : 'clients'}`}
              {connectedCount > 0 && pendingCount > 0 && ', '}
              {pendingCount > 0 && `${pendingCount} pending ${pendingCount === 1 ? 'invitation' : 'invitations'}`}
            </p>
          )}
        </div>
        {hasAny && onInviteClick && (
          <Button
            variant="outline"
            size="sm"
            onClick={onInviteClick}
            className="gap-2"
          >
            <Users className="w-4 h-4" />
            Invite More
          </Button>
        )}
      </div>

      <ReferralLinkPanel
        referralLink={referralLink}
        isLoading={isLoadingReferralLink}
        isError={isReferralLinkError}
        copied={copied}
        onCopy={handleCopyReferralLink}
        errorMessage="Unable to load your referral link. Please try again later."
        emptyMessage="Your referral link is being set up. Please check back shortly."
      />

      {!hasAny ? (
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-sage-bg rounded-full flex items-center justify-center mx-auto mb-4">
            <Users className="w-8 h-8 text-sage" />
          </div>
          <h4 className="text-text-primary mb-2">No clients yet</h4>
          <p className="text-sm text-text-secondary mb-6">
            Start building your client base by inviting clients to join bQuest
          </p>
          {onInviteClick && (
            <Button
              onClick={onInviteClick}
              className="bg-bquest-green hover:bg-bquest-green/90 gap-2"
            >
              <Users className="w-4 h-4" />
              Invite Clients
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-6">
          {/* Connected Clients Section */}
          {hasClients && (
            <SPConnectedClientsList
              clients={clients}
              onMessageClick={onMessageClick}
            />
          )}

          {/* Pending Invitations Section */}
          {hasPending && (
            <SPPendingInvitationsList
              invitations={invitations}
              cooldowns={cooldowns}
              resendingId={resendingId}
              onResend={handleResend}
            />
          )}
        </div>
      )}
    </Card>
  );
}
